import { Injectable, signal, inject } from "@angular/core";
import { DateTime, DateTimeUnit } from "luxon";
import { forkJoin } from "rxjs";
import { ASSET_TYPES } from "../../../../../enums/assetTypes.enum";
import { Customer } from "../../../../../models/api/asset/customer";

import { EnergyToolExtractionService } from "../../../../../services/state/energy-tool-extraction/energy-tool-extraction.service";

import { TelemetryService } from "../../../../../services/api/telemetry/telemetry.service";
import { EnergyToolUtilsService } from "../../../../../services/utils/energy-tool-utils/energy-tool-utils.service";
import { EnergyToolUpdateService } from "../../../../../services/state/energy-tool-update/energy-tool-update.service";
import { AssetService } from "../../../../../services/api/asset/asset.service";

@Injectable()
export class CustomerEnergyToolService {
  // State signals for Customer Energy Tool UI:
  private readonly _barChartEnergyLoss = signal<any>(null);
  readonly barChartEnergyLoss = this._barChartEnergyLoss.asReadonly();

  private readonly _lineChartEnergyLoss = signal<any>(null);
  readonly lineChartEnergyLoss = this._lineChartEnergyLoss.asReadonly();

  private readonly _doughnutChartEnergyLoss = signal<any>(null);
  readonly doughnutChartEnergyLoss = this._doughnutChartEnergyLoss.asReadonly();

  private readonly _energyLoss = signal<number | null>(null);
  readonly energyLoss = this._energyLoss.asReadonly();

  private readonly _energyCost = signal<number | null>(null);
  readonly energyCost = this._energyCost.asReadonly();

  private readonly _expectedEnergyLossPerDay = signal<number | null>(null);
  readonly expectedEnergyLossPerDay = this._expectedEnergyLossPerDay.asReadonly();

  private readonly _customerLabel = signal<string | null>(null);
  readonly customerLabel = this._customerLabel.asReadonly();

  // Services needed for logic of this facade:
  private telemetryService = inject(TelemetryService);
  private assetService = inject(AssetService);

  private energyExtraction = inject(EnergyToolExtractionService);
  private energyUpdate = inject(EnergyToolUpdateService);
  private energyUtils = inject(EnergyToolUtilsService);

  /**
   * Resets all state signals to null.
   */
  private resetStates() {
    this._barChartEnergyLoss.set(null);
    this._lineChartEnergyLoss.set(null);
    this._energyLoss.set(null);
    this._doughnutChartEnergyLoss.set(null);
    this._energyCost.set(null);
    this._expectedEnergyLossPerDay.set(null);
    this._customerLabel.set(null);
  }

  /**
   * Loads initial state of the Customer Energy Tool.
   * @param customerId - The ID of the Customer asset.
   */
  public loadInitialState(customerId: string) {
    this.resetStates();
    const now = DateTime.now().toMillis();
    const startOfMonth = DateTime.now().startOf("month").toMillis();

    // Preparing all needed observables:
    const energyLoss$ = this.fetchCustomerEnergyLoss(customerId, startOfMonth, now);

    const childrenEnergyLoss$ = this.fetchChildrenEnergyLoss(customerId, startOfMonth, now);

    const customerAsset$ = this.fetchCustomerAsset(customerId);

    // Subscription:
    forkJoin({
      energyLoss: energyLoss$,
      childrenEnergyLoss: childrenEnergyLoss$,
      customerAsset: customerAsset$,
    }).subscribe({
      next: (res) => {
        const customerEnergyLoss = res.energyLoss;
        const childrenEnergyLoss = res.childrenEnergyLoss;
        const customerAsset = res.customerAsset as Customer;

        this._customerLabel.set(customerAsset.label);
        this.energyExtraction.extractAndSetBarChartData(
          childrenEnergyLoss,
          false,
          startOfMonth,
          "month",
          this._barChartEnergyLoss.set.bind(this._barChartEnergyLoss)
        );
        this.energyExtraction.extractAndSetLineChartData(
          customerEnergyLoss?.energy_loss,
          customerEnergyLoss?.normal_energy_loss,
          startOfMonth,
          "month",
          this._lineChartEnergyLoss.set.bind(this._lineChartEnergyLoss)
        );

        this.energyExtraction.extractAndSetDoughnutChart(
          childrenEnergyLoss,
          this._doughnutChartEnergyLoss.set.bind(this._doughnutChartEnergyLoss)
        );

        this.energyExtraction.extractAndSetTotalEnergyLoss(
          customerEnergyLoss,
          "today",
          this._energyLoss.set.bind(this._energyLoss)
        );
        this.energyExtraction.extractAndSetEnergyCost(
          customerEnergyLoss,
          "today",
          this._energyCost.set.bind(this._energyCost)
        );
        this.energyExtraction.extractAndSetExpectedEnergyLossPerDay(
          customerAsset,
          this._expectedEnergyLossPerDay.set.bind(this._expectedEnergyLossPerDay)
        );
      },
    });
  }

  /**
   * Updates energy loss for a given time window.
   * @param customerId - The ID of the Customer asset.
   * @param timeWindow - The time window to update energy loss for.
   */
  public updateEnergyLossForTimeWindow(customerId: string, timeWindow: string) {
    this.energyUpdate.updateEnergyLossForTimeWindow(
      customerId,
      timeWindow,
      this._energyLoss.set.bind(this._energyLoss),
      this._energyCost.set.bind(this._energyCost),
      this.fetchCustomerEnergyLoss.bind(this)
    );
  }

  /**
   * Updates bar chart data for a given time range.
   * @param start - The start of the time range.
   * @param end - The end of the time range.
   * @param timeFrame - The time frame to update bar chart data for.
   * @param customerId - The ID of the Customer asset.
   * @param assetLabel
   */
  public updateBarChartForTimeRange(
    start: number,
    end: number,
    timeFrame: DateTimeUnit,
    customerId: string,
    assetLabel: string | undefined
  ) {
    this.energyUpdate.updateBarChartForTimeWindow(
      customerId,
      assetLabel,
      start,
      end,
      timeFrame,
      false,
      false,
      this._barChartEnergyLoss.set.bind(this._barChartEnergyLoss),
      this.fetchChildrenEnergyLoss.bind(this)
    );
  }

  /**
   * Updates doughnut chart data for a given time range.
   * @param customerId - The ID of the Customer asset.
   * @param timeFrame - The time frame to update doughnut chart data for.
   */
  public updateDoughnutChartForTimeRange(customerId: string, timeFrame: string) {
    this.energyUpdate.updateDoughnutChartForTimeWindow(
      customerId,
      timeFrame,
      this._doughnutChartEnergyLoss.set.bind(this._doughnutChartEnergyLoss),
      this.fetchChildrenEnergyLoss.bind(this)
    );
  }

  /**
   * Updates line chart data for a given time range.
   * @param start - The start of the time range.
   * @param end - The end of the time range.
   * @param timeFrame - The time frame to update line chart data for.
   * @param customerId - The ID of the Customer asset.
   */
  public updateLineChartForTimeRange(start: number, end: number, timeFrame: DateTimeUnit, customerId: string) {
    this.energyUpdate.updateLineChartForTimeWindow(
      customerId,
      start,
      end,
      timeFrame,
      this._lineChartEnergyLoss.set.bind(this._lineChartEnergyLoss),
      this.fetchCustomerEnergyLoss.bind(this)
    );
  }

  /**
   * Fetches customer energy loss data.
   * @param customerId - The ID of the Customer asset.
   * @param start - The start time.
   * @param end - The end time.
   * @returns An observable of the customer energy loss data.
   */
  private fetchCustomerEnergyLoss(customerId: string, start: number, end: number) {
    return this.telemetryService.getTimeseries(
      customerId,
      ASSET_TYPES.CUSTOMER,
      "energy_loss,normal_energy_loss,energy_loss_costs",
      start,
      end
    );
  }

  /**
   * Fetches customer asset details.
   * @param customerId - The ID of the Customer asset.
   * @returns An observable of the customer asset details.
   */
  private fetchCustomerAsset(customerId: string) {
    return this.assetService.getAssetDetails(customerId, ASSET_TYPES.CUSTOMER);
  }

  /**
   * Fetches children energy loss data.
   * @param customerId - The ID of the Customer asset.
   * @param start - The start time.
   * @param end - The end time.
   * @returns An observable of the children energy loss data.
   */
  private fetchChildrenEnergyLoss(customerId: string, start: number, end: number) {
    return this.energyUtils.getChildrenEnergyLossObservable(customerId, ASSET_TYPES.CUSTOMER, start, end);
  }
}
